import { createFileRoute, Link } from "@tanstack/react-router";
import { Phone, Mail, Globe, ArrowRight } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import reviewsHeroBg from "@/assets/about-hero-bg.png";
import { SITE_URL, canonical, seoCanonicalLink, seoOgUrl } from "@/lib/seo";

export const Route = createFileRoute("/reviews")({
  component: ReviewsPage,
  head: () => ({
    meta: [
      { title: "SimpleTech Reviews — 5.0 Stars from 32 Local Clients" },
      { name: "description", content: "Read verified reviews of SimpleTech from businesses, professionals and seniors across Montclair and the Inland Empire. Rated 5.0 from 32 reviews." },
      { property: "og:title", content: "SimpleTech Reviews — 5.0 Stars from 32 Local Clients" },
      { property: "og:description", content: "Real customer reviews for SimpleTech IT services." },
      seoOgUrl("/reviews"),
    ],
    links: [
      seoCanonicalLink("/reviews"),
    ],
  }),
});

function ReviewsPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader active="reviews" />

      {/* Hero */}
      <section className="relative min-h-[230px] md:min-h-[315px] text-white overflow-hidden flex items-center">
        <div
          className="absolute inset-0 bg-cover bg-bottom"
          style={{ backgroundImage: `url(${reviewsHeroBg})` }}
        />
        <div className="absolute inset-0 bg-[#0b0a3d]/40" />
        <div className="relative max-w-7xl mx-auto px-8 w-full py-12 flex items-center justify-between">
          <h1 className="text-3xl md:text-5xl font-extrabold text-white max-w-3xl leading-tight">
            SimpleTech Helper Reviews — 5.0 Stars from 32 Montclair and Inland Empire Clients
          </h1>
          <nav aria-label="Breadcrumb" className="hidden md:block">
            <ol className="flex items-center gap-2 text-white/80 text-sm font-medium tracking-wide">
              <li><Link to="/" className="hover:text-white transition-colors">HOME</Link></li>
              <li aria-hidden className="text-white/50">•</li>
              <li className="text-white">REVIEWS</li>
            </ol>
          </nav>
        </div>
      </section>

      {/* TrustIndex Widget */}
      <section className="relative py-16 md:py-24 bg-slate-50">
        <div className="relative max-w-7xl mx-auto px-6">
          <div
            className="max-w-6xl mx-auto"
            dangerouslySetInnerHTML={{
              __html: `<script defer async src='https://cdn.trustindex.io/loader.js?ab5d6df209ff2589fe968d64273'></script>`,
            }}
          />
        </div>
      </section>

      {/* CTA — after reviews */}
      <section className="relative py-20 md:py-28 bg-[#0b0f3d]">
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
            {/* Left: copy */}
            <div className="text-white">
              <p className="text-sm font-bold tracking-[0.25em] text-sky-300 uppercase mb-4">
                Get The Help You Need — Fast And Easy
              </p>
              <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight leading-tight">
                The Life-Changing<br />Power Of SimpleTech
              </h2>
              <p className="mt-6 text-white/80 leading-relaxed max-w-xl">
                SimpleTech provides a wide range of services that can meet any need. From network and server setup to hardware information technology repairs and maintenance services, they have innovative solutions tailored specifically to businesses, tech consumers, and seniors alike.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 mt-8 bg-sky-600 hover:bg-sky-700 text-white font-extrabold tracking-wider px-8 py-4 rounded-md transition-colors"
              >
                Contact Us Today
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            {/* Right: contact methods */}
            <div className="space-y-6">
              {[
                { icon: Globe, title: "Chat With Us:", value: "Speak to our friendly team via live chat." },
                { icon: Mail, title: "Email Us:", value: "contact@simpletechhelper.com" },
                { icon: Phone, title: "Call Us:", value: "Call our team Mon-Fri from 7am to 5pm.\n\n(909) 255-9112" },
              ].map(({ icon: Icon, title, value }) => (
                <div
                  key={title}
                  className="flex items-start gap-5 bg-white/5 border border-white/10 rounded-xl p-5 backdrop-blur-sm"
                >
                  <div className="w-12 h-12 rounded-full bg-sky-400/20 grid place-items-center shrink-0">
                    <Icon className="w-5 h-5 text-sky-300" />
                  </div>
                  <div>
                    <div className="text-lg font-extrabold text-white">{title}</div>
                    <div className="text-white/70 whitespace-pre-line">{value}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
